import React, { useState, useEffect, useCallback } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLink,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faXmark,
  faCheckCircle,
  faClock,
  faTimesCircle,
  faHourglassEnd,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

const STATUS_CLS = {
  pending:  "bg-yellow-100 text-yellow-700",
  accepted: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-600",
  expired:  "bg-gray-100 text-gray-500",
};

const STATUS_ICON = {
  pending:  faClock,
  accepted: faCheckCircle,
  declined: faTimesCircle,
  expired:  faHourglassEnd,
};

const ConnectsList = () => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusCounts, setStatusCounts] = useState({ pending: 0, accepted: 0, declined: 0, expired: 0 });
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ status: "", startDate: "", endDate: "", search: "" });
  const [activeFilters, setActiveFilters] = useState({ status: "", startDate: "", endDate: "", search: "" });
  const limit = 20;

  const fetchConnects = useCallback(async (page, f) => {
    const controller = new AbortController();
    setLoading(true);
    try {
      const params = { page, limit };
      if (f.status) params.status = f.status;
      if (f.startDate) params.startDate = f.startDate;
      if (f.endDate) params.endDate = f.endDate;
      if (f.search) params.search = f.search;

      const res = await func.get(urls.LIST_CONNECTS_URL, { params, signal: controller.signal });
      if (res?.status === 200) {
        setConnections(res.data.connections || []);
        setTotalPages(res.data.totalPages || 1);
        setStatusCounts(res.data.statusCounts || {});
      } else {
        toast.error("Error fetching connections");
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      toast.error("Network error fetching connections");
    } finally {
      setLoading(false);
    }
    return () => controller.abort();
  }, []);

  useEffect(() => {
    fetchConnects(currentPage, activeFilters);
  }, [currentPage, activeFilters, fetchConnects]);

  const applyFilters = () => {
    setActiveFilters({ ...filters });
    setCurrentPage(1);
    setShowFilter(false);
  };

  const resetFilters = () => {
    const empty = { status: "", startDate: "", endDate: "", search: "" };
    setFilters(empty);
    setActiveFilters(empty);
    setCurrentPage(1);
    setShowFilter(false);
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p >= currentPage - 2 && p <= currentPage + 2
  );

  const fmt = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  };

  const CountCard = ({ label, count, status }) => (
    <div className="bg-white rounded-xl p-4 shadow text-center">
      <FontAwesomeIcon icon={STATUS_ICON[status] || faLink} className={`text-xl mb-1 ${
        status === "accepted" ? "text-green-500" :
        status === "declined" ? "text-red-500" :
        status === "pending" ? "text-yellow-500" : "text-gray-400"
      }`} />
      <p className="text-2xl font-bold text-slate-800">{(count || 0).toLocaleString()}</p>
      <p className="text-xs text-slate-500 font-medium capitalize">{label}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faLink} className="text-white text-xl" />
            <h1 className="text-xl font-bold text-white">Connects</h1>
          </div>
          <button
            onClick={() => setShowFilter(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 text-white text-sm rounded-lg transition"
          >
            <FontAwesomeIcon icon={faFilter} />
            Filter
          </button>
        </div>

        {/* Active filter chips */}
        {activeFilters.search && (
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white border border-white/30">
              Phone: {activeFilters.search}
              <button
                onClick={() => {
                  const next = { ...activeFilters, search: "" };
                  setFilters(next);
                  setActiveFilters(next);
                  setCurrentPage(1);
                }}
                className="ml-0.5 hover:text-red-200 transition"
              >
                <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
              </button>
            </span>
          </div>
        )}

        {/* Status Count Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <CountCard label="Pending" count={statusCounts.pending} status="pending" />
          <CountCard label="Accepted" count={statusCounts.accepted} status="accepted" />
          <CountCard label="Declined" count={statusCounts.declined} status="declined" />
          <CountCard label="Expired" count={statusCounts.expired} status="expired" />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sender Phone</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Receiver Phone</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sent At</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Accepted At</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Expires At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="px-5 py-3">
                          <div className="h-4 bg-slate-100 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : connections.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-slate-400 text-sm">
                      No connections found
                    </td>
                  </tr>
                ) : (
                  connections.map((c, idx) => (
                    <tr key={c._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 text-slate-400 text-xs">
                        {(currentPage - 1) * limit + idx + 1}
                      </td>
                      <td className="px-5 py-3 font-medium text-slate-800">
                        {c.senderUserId?._phoneNumber || "—"}
                      </td>
                      <td className="px-5 py-3 font-medium text-slate-800">
                        {c.receiverUserId?._phoneNumber || "—"}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_CLS[c.status] || "bg-gray-100 text-gray-600"}`}>
                          <FontAwesomeIcon icon={STATUS_ICON[c.status] || faLink} className="text-[10px]" />
                          {c.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{fmt(c.createdAt)}</td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{fmt(c.acceptedAt)}</td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{fmt(c.expiresAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-400">Page {currentPage} of {totalPages}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded text-slate-400 hover:text-indigo-600 disabled:opacity-30"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                </button>
                {pages.map((p) => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-7 h-7 rounded text-xs font-medium transition ${
                      p === currentPage ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded text-slate-400 hover:text-indigo-600 disabled:opacity-30"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-slate-800">Filter Connects</h2>
              <button onClick={() => setShowFilter(false)} className="text-slate-400 hover:text-slate-600">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Search by phone number</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                  placeholder="+91XXXXXXXXXX"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="declined">Declined</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Start Date</label>
                <input type="date" value={filters.startDate}
                  onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">End Date</label>
                <input type="date" value={filters.endDate}
                  onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={resetFilters} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition">
                Reset
              </button>
              <button onClick={applyFilters} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectsList;
